import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  fetchAllEntries, fetchAllProfiles, adminDeleteEntry,
  togglePublic, setUserRole, setUserPlan, setUserTrial, banUser, fetchAdminStats,
  fetchFlaggedEntries, fetchEntryReports, dismissReports, adminHideEntry, adminRestoreEntry,
} from '../lib/supabase';
import { TYPE_ICONS, formatDate } from '../constants';

// ── Stat card ─────────────────────────────────────────────────
function StatCard({ label, value, sub, color }) {
  return (
    <div style={{
      background: 'var(--surface)', border: '0.5px solid var(--border)',
      borderRadius: 'var(--radius)', padding: '16px 20px', flex: 1, minWidth: 120,
    }}>
      <div style={{
        fontSize: 28, fontWeight: 500, color: color || 'var(--text)',
        letterSpacing: '-0.02em', marginBottom: 3,
      }}>{value}</div>
      <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--muted)', marginBottom: 2 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--dim)' }}>{sub}</div>}
    </div>
  );
}

// ── User action menu ──────────────────────────────────────────
function UserActions({ profile, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user: currentUser } = useAuth();
  const isSelf = profile.id === currentUser?.id;

  const run = async (fn) => {
    setLoading(true);
    setOpen(false);
    try { await fn(); } catch (e) { alert(e.message); }
    setLoading(false);
  };

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        disabled={loading || isSelf}
        style={{
          ...s.actionBtn,
          opacity: (loading || isSelf) ? 0.4 : 1,
          cursor: (loading || isSelf) ? 'not-allowed' : 'pointer',
        }}>
        {loading ? '...' : 'Actions ▾'}
      </button>

      {open && (
        <div style={{
          position: 'absolute', right: 0, top: '100%', marginTop: 4,
          background: 'var(--surface)', border: '0.5px solid var(--border2)',
          borderRadius: 'var(--radius)', zIndex: 20, minWidth: 160,
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)', overflow: 'hidden',
        }}>
          {/* Plan actions */}
          {profile.plan !== 'pro' && (
            <div style={s.menuItem} onClick={() => run(() => setUserPlan(profile.id, 'pro').then(() => onUpdate(profile.id, { plan: 'pro', trial_expires_at: null })))}>
              ⚡ Give Pro
            </div>
          )}
          {profile.plan === 'pro' && (
            <div style={s.menuItem} onClick={() => run(() => setUserPlan(profile.id, 'free').then(() => onUpdate(profile.id, { plan: 'free', trial_expires_at: null })))}>
              ○ Remove Pro
            </div>
          )}
          {/* Trial actions */}
          {!profile.trial_expires_at && profile.plan !== 'pro' && (
            <div style={s.menuItem} onClick={() => {
              const days = parseInt(prompt('Trial length in days?', '7'), 10);
              if (!days || isNaN(days)) return;
              const expires = new Date(Date.now() + days * 86400000).toISOString();
              run(() => setUserTrial(profile.id, expires).then(() => onUpdate(profile.id, { trial_expires_at: expires })));
            }}>
              🕐 Start trial
            </div>
          )}
          {profile.trial_expires_at && profile.plan !== 'pro' && (
            <div style={s.menuItem} onClick={() => run(() => setUserTrial(profile.id, null).then(() => onUpdate(profile.id, { trial_expires_at: null })))}>
              ✕ End trial
            </div>
          )}

          {/* Role actions */}
          {profile.role !== 'admin' && profile.role !== 'banned' && (
            <div style={s.menuItem} onClick={() => run(() => setUserRole(profile.id, 'admin').then(() => onUpdate(profile.id, { role: 'admin' })))}>
              🛡 Make admin
            </div>
          )}
          {profile.role === 'admin' && (
            <div style={s.menuItem} onClick={() => run(() => setUserRole(profile.id, 'user').then(() => onUpdate(profile.id, { role: 'user' })))}>
              Remove admin
            </div>
          )}

          <div style={{ height: '0.5px', background: 'var(--border)', margin: '2px 0' }} />

          {/* Ban / unban */}
          {profile.role !== 'banned' ? (
            <div style={{ ...s.menuItem, color: 'var(--danger)' }}
              onClick={() => {
                if (!window.confirm(`Ban ${profile.username}? They will lose access.`)) return;
                run(() => banUser(profile.id).then(() => onUpdate(profile.id, { role: 'banned' })));
              }}>
              🚫 Ban user
            </div>
          ) : (
            <div style={{ ...s.menuItem, color: '#5dcaa5' }}
              onClick={() => run(() => setUserRole(profile.id, 'user').then(() => onUpdate(profile.id, { role: 'user' })))}>
              ✓ Unban user
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main admin page ───────────────────────────────────────────
export default function AdminPage() {
  const { isAdmin, isLoading } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab]           = useState('overview');
  const [entries, setEntries]   = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [stats, setStats]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [flagged, setFlagged]   = useState([]);
  const [expandedReport, setExpandedReport] = useState(null);
  const [reportDetails, setReportDetails]   = useState({});

  useEffect(() => {
    if (!isLoading && !isAdmin) navigate('/app');
  }, [isAdmin, isLoading, navigate]);

  useEffect(() => {
    if (!isAdmin) return;
    setLoading(true);
    Promise.all([fetchAllEntries(), fetchAllProfiles(), fetchAdminStats(), fetchFlaggedEntries()])
      .then(([e, p, st, fl]) => { setEntries(e); setProfiles(p); setStats(st); setFlagged(fl || []); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [isAdmin]);

  const updateProfile = (id, fields) => {
    setProfiles(prev => prev.map(p => p.id === id ? { ...p, ...fields } : p));
  };

  const handleDeleteEntry = async (id) => {
    if (!window.confirm('Delete this entry permanently?')) return;
    await adminDeleteEntry(id);
    setEntries(prev => prev.filter(e => e.id !== id));
  };

  const handleTogglePublic = async (entry) => {
    await togglePublic(entry.id, !entry.is_public);
    setEntries(prev => prev.map(e => e.id === entry.id ? { ...e, is_public: !e.is_public } : e));
  };

  const filteredEntries = useMemo(() => entries.filter(e =>
    !search ||
    e.title?.toLowerCase().includes(search.toLowerCase()) ||
    e.model?.toLowerCase().includes(search.toLowerCase()) ||
    e.profiles?.username?.toLowerCase().includes(search.toLowerCase())
  ), [entries, search]);

  const filteredProfiles = useMemo(() => profiles.filter(p =>
    !userSearch ||
    p.username?.toLowerCase().includes(userSearch.toLowerCase()) ||
    p.email?.toLowerCase().includes(userSearch.toLowerCase())
  ), [profiles, userSearch]);

  if (isLoading || loading) return (
    <div style={s.page}><div style={{ color: 'var(--muted)', textAlign: 'center', paddingTop: 80 }}>Loading...</div></div>
  );

  const TABS = ['overview', 'entries', 'users', 'reports'];

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <div style={s.title}>Admin panel</div>
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>CopyPasteGenius</div>
        </div>
        <button onClick={() => navigate('/app')} style={s.backBtn}>← Back to app</button>
      </div>

      {/* Tabs */}
      <div style={s.tabs}>
        {TABS.map(t => (
          <button key={t} style={s.tabBtn(tab === t)} onClick={() => setTab(t)}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {/* ── Overview ── */}
      {tab === 'overview' && stats && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          <div>
            <div style={s.sectionTitle}>Entries</div>
            <div style={s.statRow}>
              <StatCard label="Total entries"   value={stats.entries.total}   sub={`${stats.entries.today} added today`} />
              <StatCard label="Public"          value={stats.entries.public}  color="#5dcaa5" />
              <StatCard label="Private"         value={stats.entries.private} color="var(--muted)" />
              <StatCard label="This week"       value={stats.entries.week}    />
            </div>
          </div>

          <div>
            <div style={s.sectionTitle}>Users</div>
            <div style={s.statRow}>
              <StatCard label="Total users"     value={stats.users.total}        sub={`${stats.users.newThisMonth} this month`} />
              <StatCard label="Free plan"       value={stats.users.free}         color="var(--muted)" />
              <StatCard label="Pro plan"        value={stats.users.pro}          color="var(--accent)" sub="$9/mo each" />
              <StatCard label="Admins"          value={stats.users.admin}        color="#ef9f27" />
              {stats.users.banned > 0 &&
                <StatCard label="Banned"        value={stats.users.banned}       color="var(--danger)" />
              }
            </div>
          </div>

          <div>
            <div style={s.sectionTitle}>Revenue estimate</div>
            <div style={s.statRow}>
              <StatCard
                label="Monthly (est.)"
                value={`$${stats.users.pro * 9}`}
                sub={`${stats.users.pro} Pro users × $9`}
                color="#5dcaa5"
              />
              <StatCard
                label="Annual run rate"
                value={`$${stats.users.pro * 9 * 12}`}
                sub="Based on current Pro users"
              />
              <StatCard
                label="Free → Pro rate"
                value={stats.users.total > 0 ? `${Math.round((stats.users.pro / stats.users.total) * 100)}%` : '—'}
                sub="Conversion rate"
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Entries ── */}
      {tab === 'entries' && (
        <>
          <div style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              style={{ ...s.search, flex: 1, marginBottom: 0 }}
              placeholder="Search by title, model, or username..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <div style={{ fontSize: 12, color: 'var(--dim)', whiteSpace: 'nowrap' }}>
              {filteredEntries.length} of {entries.length}
            </div>
          </div>

          <div style={s.table}>
            <div style={s.thead}>
              <div style={{ flex: 3 }}>Title</div>
              <div style={{ flex: 1 }}>Type</div>
              <div style={{ flex: 2 }}>Model</div>
              <div style={{ flex: 1.5 }}>User</div>
              <div style={{ flex: 1 }}>Visibility</div>
              <div style={{ flex: 1 }}>Date</div>
              <div style={{ flex: 1.5 }}>Actions</div>
            </div>
            {filteredEntries.length === 0 && (
              <div style={{ padding: '24px', textAlign: 'center', color: 'var(--dim)', fontSize: 13 }}>No entries found</div>
            )}
            {filteredEntries.map(entry => (
              <div key={entry.id} style={s.trow}>
                <div style={{ flex: 3, fontSize: 13, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {TYPE_ICONS[entry.type]} {entry.title}
                </div>
                <div style={{ flex: 1 }}>
                  <span className={`tag tag-${entry.type}`} style={{ fontSize: 10 }}>{entry.type}</span>
                </div>
                <div style={{ flex: 2, fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {entry.model}
                </div>
                <div style={{ flex: 1.5, fontSize: 12, color: 'var(--muted)' }}>
                  {entry.profiles?.username || '—'}
                </div>
                <div style={{ flex: 1 }}>
                  <span style={{
                    fontSize: 10, padding: '2px 7px', borderRadius: 4,
                    background: entry.is_public ? 'rgba(29,158,117,0.15)' : 'rgba(95,94,90,0.15)',
                    color: entry.is_public ? '#5dcaa5' : 'var(--dim)',
                  }}>
                    {entry.is_public ? 'public' : 'private'}
                  </span>
                </div>
                <div style={{ flex: 1, fontSize: 11, color: 'var(--dim)' }}>{formatDate(entry.created_at)}</div>
                <div style={{ flex: 1.5, display: 'flex', gap: 6 }}>
                  <button onClick={() => handleTogglePublic(entry)} style={s.actionBtn}>
                    {entry.is_public ? 'Hide' : 'Show'}
                  </button>
                  <button onClick={() => handleDeleteEntry(entry.id)}
                    style={{ ...s.actionBtn, color: 'var(--danger)', borderColor: 'rgba(226,75,74,0.3)' }}>
                    Del
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── Users ── */}
      {tab === 'users' && (
        <>
          <div style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              style={{ ...s.search, flex: 1, marginBottom: 0 }}
              placeholder="Search by username or email..."
              value={userSearch}
              onChange={e => setUserSearch(e.target.value)}
            />
            <div style={{ fontSize: 12, color: 'var(--dim)', whiteSpace: 'nowrap' }}>
              {filteredProfiles.length} of {profiles.length}
            </div>
          </div>

          <div style={s.table}>
            <div style={s.thead}>
              <div style={{ flex: 2 }}>Username</div>
              <div style={{ flex: 1 }}>Plan</div>
              <div style={{ flex: 1 }}>Role</div>
              <div style={{ flex: 1 }}>Entries</div>
              <div style={{ flex: 1.5 }}>Joined</div>
              <div style={{ flex: 1.5 }}>Actions</div>
            </div>
            {filteredProfiles.length === 0 && (
              <div style={{ padding: '24px', textAlign: 'center', color: 'var(--dim)', fontSize: 13 }}>No users found</div>
            )}
            {filteredProfiles.map(p => (
              <div key={p.id} style={{
                ...s.trow,
                background: p.role === 'banned' ? 'rgba(226,75,74,0.04)' : 'transparent',
              }}>
                <div style={{ flex: 2 }}>
                  <div style={{ fontSize: 13, color: p.role === 'banned' ? 'var(--danger)' : 'var(--text)', fontWeight: 500 }}>
                    {p.username}
                    {p.role === 'banned' && <span style={{ fontSize: 10, marginLeft: 6, color: 'var(--danger)' }}>BANNED</span>}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--dim)', marginTop: 1 }}>{p.email || '—'}</div>
                </div>
                <div style={{ flex: 1 }}>
                  <span style={{
                    fontSize: 10, padding: '2px 8px', borderRadius: 10,
                    background: p.plan === 'pro' ? 'var(--accent-dim)' : 'rgba(95,94,90,0.15)',
                    border: `0.5px solid ${p.plan === 'pro' ? 'var(--accent-border)' : 'transparent'}`,
                    color: p.plan === 'pro' ? 'var(--accent)' : 'var(--dim)',
                  }}>
                    {p.plan === 'pro' ? '⚡ Pro' : '○ Free'}
                  </span>
                </div>
                <div style={{ flex: 1 }}>
                  <span style={{
                    fontSize: 10, padding: '2px 7px', borderRadius: 4,
                    background: p.role === 'admin' ? 'rgba(239,159,39,0.15)'
                      : p.role === 'banned' ? 'rgba(226,75,74,0.15)' : 'transparent',
                    color: p.role === 'admin' ? '#ef9f27'
                      : p.role === 'banned' ? 'var(--danger)' : 'var(--dim)',
                  }}>
                    {p.role}
                  </span>
                </div>
                <div style={{ flex: 1, fontSize: 12, color: 'var(--muted)' }}>
                  {p.entry_count ?? '—'}
                </div>
                <div style={{ flex: 1.5, fontSize: 11, color: 'var(--dim)' }}>
                  {formatDate(p.created_at)}
                </div>
                <div style={{ flex: 1.5 }}>
                  <UserActions profile={p} onUpdate={updateProfile} />
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      {/* ── Reports ── */}
      {tab === 'reports' && (
        <>
          {flagged.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--dim)', fontSize: 13 }}>
              ✓ No flagged content — you're all clear
            </div>
          ) : (
            <div style={s.table}>
              <div style={s.thead}>
                <div style={{ flex: 3 }}>Entry</div>
                <div style={{ flex: 1 }}>Reports</div>
                <div style={{ flex: 1 }}>Status</div>
                <div style={{ flex: 1.5 }}>Submitted by</div>
                <div style={{ flex: 1.5 }}>Flagged</div>
                <div style={{ flex: 2 }}>Actions</div>
              </div>
              {flagged.map(entry => (
                <div key={entry.id} style={{ borderBottom: '0.5px solid var(--border)' }}>
                  <div style={{ ...s.trow, minWidth: 0 }}>
                    <div style={{ flex: 3, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}>
                        {TYPE_ICONS[entry.type]} {entry.title}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>
                        {entry.prompt?.slice(0, 80)}...
                      </div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <span style={{
                        fontSize: 13, fontWeight: 500,
                        color: entry.report_count >= 5 ? 'var(--danger)' : entry.report_count >= 3 ? '#ef9f27' : 'var(--muted)',
                      }}>
                        {entry.report_count}
                      </span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <span style={{
                        fontSize: 10, padding: '2px 7px', borderRadius: 4,
                        background: entry.is_public ? 'rgba(29,158,117,0.15)' : 'rgba(226,75,74,0.12)',
                        color: entry.is_public ? '#5dcaa5' : 'var(--danger)',
                      }}>
                        {entry.hidden_by_mod ? 'mod-hidden' : entry.is_flagged && !entry.is_public ? 'auto-hidden' : 'visible'}
                      </span>
                    </div>
                    <div style={{ flex: 1.5, fontSize: 12, color: 'var(--muted)' }}>
                      @{entry.profiles?.username || '—'}
                    </div>
                    <div style={{ flex: 1.5, fontSize: 11, color: 'var(--dim)' }}>
                      {entry.flagged_at ? formatDate(entry.flagged_at) : formatDate(entry.created_at)}
                    </div>
                    <div style={{ flex: 2, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {/* Restore / Hide */}
                      {!entry.is_public ? (
                        <button style={{ ...s.actionBtn, color: '#5dcaa5', borderColor: 'rgba(29,158,117,0.3)' }}
                          onClick={async () => {
                            await adminRestoreEntry(entry.id);
                            setFlagged(prev => prev.filter(e => e.id !== entry.id));
                          }}>
                          Restore
                        </button>
                      ) : (
                        <button style={{ ...s.actionBtn, color: '#ef9f27', borderColor: 'rgba(239,159,39,0.3)' }}
                          onClick={async () => {
                            await adminHideEntry(entry.id);
                            setFlagged(prev => prev.map(e => e.id === entry.id ? { ...e, is_public: false, hidden_by_mod: true } : e));
                          }}>
                          Hide
                        </button>
                      )}
                      {/* Dismiss all reports */}
                      <button style={s.actionBtn}
                        onClick={async () => {
                          await dismissReports(entry.id);
                          setFlagged(prev => prev.filter(e => e.id !== entry.id));
                        }}>
                        Dismiss
                      </button>
                      {/* Delete */}
                      <button style={{ ...s.actionBtn, color: 'var(--danger)', borderColor: 'rgba(226,75,74,0.3)' }}
                        onClick={async () => {
                          if (!window.confirm('Permanently delete this entry?')) return;
                          await adminDeleteEntry(entry.id);
                          setFlagged(prev => prev.filter(e => e.id !== entry.id));
                        }}>
                        Delete
                      </button>
                      {/* Expand reports */}
                      <button style={s.actionBtn}
                        onClick={async () => {
                          if (expandedReport === entry.id) { setExpandedReport(null); return; }
                          setExpandedReport(entry.id);
                          if (!reportDetails[entry.id]) {
                            const details = await fetchEntryReports(entry.id);
                            setReportDetails(prev => ({ ...prev, [entry.id]: details }));
                          }
                        }}>
                        {expandedReport === entry.id ? 'Hide ▲' : 'Details ▼'}
                      </button>
                    </div>
                  </div>
                  {/* Expanded report details */}
                  {expandedReport === entry.id && reportDetails[entry.id] && (
                    <div style={{ padding: '10px 16px 14px', background: 'var(--surface2)', borderTop: '0.5px solid var(--border)' }}>
                      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--dim)', marginBottom: 8 }}>
                        Report breakdown
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {reportDetails[entry.id].map((r, i) => (
                          <span key={i} style={{
                            fontSize: 11, padding: '3px 10px', borderRadius: 20,
                            background: 'var(--surface)', border: '0.5px solid var(--border2)',
                            color: 'var(--muted)',
                          }}>
                            {r.reason}{r.details ? `: ${r.details}` : ''}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: 'var(--bg)', padding: '24px', fontFamily: 'var(--font)' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, gap: 12 },
  title: { fontSize: 22, fontWeight: 500, color: 'var(--text)', marginBottom: 4 },
  backBtn: {
    background: 'none', border: '0.5px solid var(--border2)', borderRadius: 'var(--radius-sm)',
    padding: '7px 14px', color: 'var(--muted)', fontFamily: 'var(--font)', fontSize: 13, cursor: 'pointer',
  },
  tabs: { display: 'flex', gap: 4, marginBottom: 20 },
  tabBtn: (active) => ({
    padding: '7px 18px', border: '0.5px solid var(--border2)',
    borderRadius: 'var(--radius-sm)',
    background: active ? 'var(--accent-dim)' : 'none',
    borderColor: active ? 'var(--accent-border)' : 'var(--border2)',
    color: active ? 'var(--accent)' : 'var(--muted)',
    fontFamily: 'var(--font)', fontSize: 13, cursor: 'pointer',
  }),
  sectionTitle: {
    fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.07em',
    color: 'var(--dim)', marginBottom: 10,
  },
  statRow: { display: 'flex', gap: 10, flexWrap: 'wrap' },
  search: {
    width: '100%', background: 'var(--surface)', border: '0.5px solid var(--border2)',
    borderRadius: 'var(--radius-sm)', padding: '9px 14px', color: 'var(--text)',
    fontFamily: 'var(--font)', fontSize: 13, outline: 'none', marginBottom: 12,
  },
  table: {
    background: 'var(--surface)', border: '0.5px solid var(--border)',
    borderRadius: 'var(--radius)', overflow: 'auto',
  },
  thead: {
    display: 'flex', gap: 12, padding: '10px 16px',
    background: 'var(--surface2)', borderBottom: '0.5px solid var(--border)',
    fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--dim)',
    minWidth: 600,
  },
  trow: {
    display: 'flex', gap: 12, padding: '12px 16px',
    borderBottom: '0.5px solid var(--border)', alignItems: 'center',
    minWidth: 600,
  },
  actionBtn: {
    background: 'none', border: '0.5px solid var(--border2)', borderRadius: 4,
    padding: '4px 10px', fontFamily: 'var(--font)', fontSize: 11,
    color: 'var(--muted)', cursor: 'pointer', whiteSpace: 'nowrap',
  },
  menuItem: {
    padding: '9px 14px', fontSize: 13, color: 'var(--text)',
    cursor: 'pointer', borderBottom: '0.5px solid var(--border)',
    transition: 'background 0.1s',
  },
};
